// @givmo/fundraisers-module — the AI email designer (EMC-WEB10; D-EMC-20 (a); mockups 14a–14d).
//
// One campaign email, written with Givmo's assistant (the console's panel) beside its live preview. The assistant writes the email
// through the campaign email service's own change operation on the backend's `email_designer` audience
// (`email_designer_tools.py`), so the renderer's rules (the footer, links and images, the sanitizer, the
// contrast floors) hold for every word it writes. A person can also tweak it by hand in the preview (D-EMC-20
// revised by Jonathan, 2026-10-31: words, the subject and preview text, a block's place, its removal and its
// photo; `email-hand-editor.tsx`), through the same route and the same rules; the layout and the design stay
// the assistant's. The page's other writes: Undo and Redo above the email, over the assistant's changes and
// hand edits alike (one stack), and "Send test".
//
// ══ THE ASSISTANT IS THE CONSOLE'S PANEL ═════════════════════════════════════════════════════════════
//
// The conversation is drawn in the console's ONE assistant panel (Jonathan, 2026-11-01: one assistant
// everywhere), which the Emails editor's scope opens by default beside the page; the page portals it in
// through `AssistantPageBody`, so its turns still wait on the page's hand edits and every state below
// (consent, the cap, the cut-off and refusal notices) is the page's own. The preview takes the page.
//
// ══ THE THREAD IS THE EMAIL'S ═══════════════════════════════════════════════════════════════════════
//
// The conversation is the shared assistant package's (`useAssistantThread`), on the designer's surface
// (`org_email_designer`) with the email's three ids stamped on its creation and on every send. Those ids are
// what the server reads the email by, checking the person may (a miss is refused before any turn), and they
// are the thread's match, so each email keeps its own conversation.
//
// ══ WHAT CHANGED ════════════════════════════════════════════════════════════════════════════════════
//
// Each reply that changed the email ends with its "Changes:" line (the server's facts, in the reply's own
// words); the page shows it under the reply with its label. When a turn ends, the page reads the email and
// its render again; if the email changed, what it held before goes on the undo stack and the new render is
// washed against the one before it (`email-designer-wash.ts`). An email that was empty before is not washed
// (14b draws the first draft plain).
//
// ══ HAND EDITS ══════════════════════════════════════════════════════════════════════════════════════
//
// Each saved hand edit goes on the same undo stack, unwashed (the person made it). Every act of this page
// waits for words still open to save (`whenSaved`), so a test, a turn, an undo, a change of width or a way out
// never meets the words as they were: a save the server refuses holds the act back. The assistant re-reads the
// email at every turn (its tools carry no copy across turns), and its prompt says the email may have changed
// by hand since its last reply.
//
// ══ CONSENT ═════════════════════════════════════════════════════════════════════════════════════════
//
// On the web console the organization's consent is the only step (Jonathan, 2026-10-30): its class's
// sheet, accepted once for the organization by an admin who may. The send's own refusal carries the sheet,
// which the card shows; a person who can't accept it reads who can. The person is never asked again.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { organizationDate } from '@givmo/org-console';
import {
  AssistantConsentCard,
  AssistantPageBody,
  decodeOrgConsent,
  defaultAssistantVocab,
  useAcceptOrgAssistantConsent,
  useAssistantThread,
  yieldAssistantSheet,
  type AssistantVocab,
  type ThreadMessage,
} from '@givmo/assistant-panel';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  GivmoLogomark,
  Icon,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  ArrowLeft,
  ArrowUp,
  Monitor,
  RotateCcw,
  RotateCw,
  Send,
  Smartphone,
  Sparkles,
  Square,
} from 'lucide-react';
import { EMAIL_DESIGNER_COPY as S } from './email-designer-copy';
import {
  decodeDesignerEmail,
  decodeDesignerPreview,
  decodeDesignerTest,
  designerEmailChangeable,
  designerEmailEmpty,
  seriesPosition,
  splitChangesLine,
  splitSourcesLine,
  type DesignerEmail,
} from './email-designer-decode';
import {
  fetchDesignerEmail,
  fetchDesignerPreview,
  fetchDesignerSeries,
  restoreDesignerEmail,
  saveDesignerEmail,
  sendDesignerTest,
} from './email-designer-transport';
import { HandEditLayer, InboxLineField } from './email-hand-editor';
import { historyKeyOf } from './email-hand-edit';
import { EmailPreviewStage } from './email-preview-stage';
import { EMAILS_COPY } from './emails-copy';
import { decodeEmail, defaultZone, emailStatus, refusalWords } from './emails-decode';
import { useFundraisersModule } from './fundraisers-context';
import { mayManageCampaignEmails, mayWriteFundraisers } from './fundraisers-config';
import { fundraiserOptions } from './fundraisers-queries';
import { useSenderIdentity } from './sender-identity';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import { FundraiserNotHere, FundraisersLoading, ModuleLink } from './fundraisers-state';

/** The backend's designer surface (`email_designer_surface.EMAIL_DESIGNER_SURFACE`), verbatim. */
export const EMAIL_DESIGNER_WIRE_SURFACE = 'org_email_designer';

/** What the email held before a change: what Undo puts back. */
interface Snapshot {
  subject: string;
  preheader: string;
  body: Record<string, unknown>;
  blockCount: number;
  html: string | null;
}

/** One Undo: what the email held before a change, the assistant's or a hand edit, and what it held after. */
interface UndoStep {
  before: Snapshot;
  after: DesignerEmail;
  byHand: boolean;
}

/**
 * An undo stack the page draws from, read through a ref that's current at once: an Undo pressed with words
 * still open waits for their save, and must then see the step that save just added, before any draw.
 */
function useSteps() {
  const steps = useRef<UndoStep[]>([]);
  const [, draw] = useState(0);
  const write = useCallback((next: UndoStep[] | ((stack: UndoStep[]) => UndoStep[])) => {
    steps.current = typeof next === 'function' ? next(steps.current) : next;
    draw((count) => count + 1);
  }, []);
  return [steps, write] as const;
}

/** An email as a step holds it. */
function stepOf(email: DesignerEmail, html: string | null): Snapshot {
  return {
    subject: email.subject,
    preheader: email.preheader,
    body: email.body ?? {},
    blockCount: email.blockCount,
    html,
  };
}

/** The email changed somewhere else since the step's change, so putting `before` back would overwrite it. */
class ChangedSinceError extends Error {}

const designerKeys = {
  email: (orgRef: string, fundraiserId: string, emailId: string) =>
    ['email-designer', orgRef, fundraiserId, emailId, 'email'] as const,
  preview: (orgRef: string, fundraiserId: string, emailId: string) =>
    ['email-designer', orgRef, fundraiserId, emailId, 'preview'] as const,
  series: (orgRef: string, fundraiserId: string) =>
    ['email-designer', orgRef, fundraiserId, 'series'] as const,
};

function sameContent(
  a: { subject: string; preheader: string; body: Record<string, unknown> | null },
  b: DesignerEmail,
): boolean {
  return (
    a.subject === b.subject &&
    a.preheader === b.preheader &&
    JSON.stringify(a.body) === JSON.stringify(b.body)
  );
}

/** "Tue, Dec 1 at 8:00 AM", in the email's own zone. */
function emailTimeLabel(iso: string, zone: string | null): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const options = zone === null ? {} : { timeZone: zone };
  try {
    const day = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...options,
    }).format(at);
    const time = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...options,
    }).format(at);
    return `${day} at ${time}`;
  } catch {
    return null;
  }
}

const DESIGNER_VOCAB: AssistantVocab = { ...defaultAssistantVocab(), consentScopeLine: null };

export function EmailDesignerPage({
  fundraiserId,
  emailId,
  orgLogoSrc = null,
}: {
  fundraiserId: string;
  emailId: string;
  /** The organization's own square logo (its Givmo profile's), for the inbox line's sender picture; the
   *  host supplies it, as the QR page's. Absent, the line shows the organization's initials. */
  orgLogoSrc?: string | null;
}) {
  const config = useFundraisersModule();
  const designer = config.emailDesigner ?? null;
  const orgRef = config.orgRef ?? '';
  const writable = mayWriteFundraisers(config);
  const queryClient = useQueryClient();
  const live = designer !== null && writable && orgRef.length > 0;

  const fundraiser = useQuery({ ...fundraiserOptions(orgRef, fundraiserId), enabled: live });
  const emailRead = useQuery({
    queryKey: designerKeys.email(orgRef, fundraiserId, emailId),
    queryFn: ({ signal }) => fetchDesignerEmail(orgRef, fundraiserId, emailId, { signal }),
    enabled: live,
    retry: false,
  });
  const series = useQuery({
    queryKey: designerKeys.series(orgRef, fundraiserId),
    queryFn: ({ signal }) => fetchDesignerSeries(orgRef, fundraiserId, { signal }),
    enabled: live,
    retry: false,
  });
  const previewRead = useQuery({
    queryKey: designerKeys.preview(orgRef, fundraiserId, emailId),
    queryFn: ({ signal }) => fetchDesignerPreview(orgRef, fundraiserId, emailId, { signal }),
    enabled: live,
    retry: false,
  });
  const email = useMemo(() => decodeDesignerEmail(emailRead.data), [emailRead.data]);
  // The email's state in the series' own words ("Draft", "Scheduled"), as its page and the series say it.
  const row = useMemo(() => decodeEmail(emailRead.data), [emailRead.data]);
  const preview = useMemo(() => decodeDesignerPreview(previewRead.data), [previewRead.data]);
  // A test goes out only from a domain the server can send as (review-polish A-1): until then Send
  // test waits, and says why, rather than meeting the server's refusal.
  const sender = useSenderIdentity(live ? orgRef : null);
  const testWaits =
    sender.data != null && !sender.data.canSchedule
      ? EMAILS_COPY.testWaitsOnDomain(sender.data.domain)
      : null;

  if (!live) {
    return <DesignerUnavailable fundraiserId={fundraiserId} />;
  }
  if (emailRead.isPending || fundraiser.isPending) {
    return (
      <div className="fundraisers-module mx-auto w-full max-w-[1320px] px-16 py-24 sm:px-24">
        <FundraisersLoading />
      </div>
    );
  }
  if (email === null) {
    // A link from another of the person's organizations: this one holds neither the email nor its
    // fundraiser, so say where it is.
    return isGivmoError(fundraiser.error) && fundraiser.error.status === 404 ? (
      <div
        className="fundraisers-module mx-auto w-full max-w-[1320px] px-16 py-24 sm:px-24"
        {...testIdProp('page-email-designer')}
      >
        <FundraiserNotHere fundraiserId={fundraiserId} />
      </div>
    ) : (
      <DesignerUnavailable fundraiserId={fundraiserId} />
    );
  }
  return (
    <DesignerScreen
      key={emailId}
      fundraiserId={fundraiserId}
      emailId={emailId}
      orgRef={orgRef}
      orgName={config.orgName}
      campaignName={fundraiser.data?.name ?? S.backFallback}
      position={seriesPosition(series.data, emailId)}
      status={row === null ? null : EMAILS_COPY.status[emailStatus(row)]}
      untouchedCopy={row?.untouchedCopy === true}
      testWaits={testWaits}
      senderPending={sender.isPending}
      email={email}
      previewHtml={preview.html}
      previewPending={previewRead.isPending}
      fromName={preview.fromName ?? config.orgName}
      fromAddress={preview.fromAddress}
      orgLogoSrc={orgLogoSrc}
      designer={designer}
      canUploadCollateral={config.permissions?.includes('profile:write') === true}
      canChangeScheduled={mayManageCampaignEmails(config)}
      orgToday={organizationDate(defaultZone(undefined, config.orgTimeZone))}
      onNavigate={config.onNavigate}
      refresh={async () => {
        const [nextEmail, nextPreview] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: designerKeys.email(orgRef, fundraiserId, emailId),
            queryFn: ({ signal }) => fetchDesignerEmail(orgRef, fundraiserId, emailId, { signal }),
            staleTime: 0,
          }),
          queryClient.fetchQuery({
            queryKey: designerKeys.preview(orgRef, fundraiserId, emailId),
            queryFn: ({ signal }) =>
              fetchDesignerPreview(orgRef, fundraiserId, emailId, { signal }),
            staleTime: 0,
          }),
        ]);
        return {
          email: decodeDesignerEmail(nextEmail),
          html: decodeDesignerPreview(nextPreview).html,
        };
      }}
    />
  );
}

function DesignerUnavailable({ fundraiserId }: { fundraiserId: string }) {
  return (
    <div
      className="fundraisers-module mx-auto w-full max-w-[1320px] px-16 py-24 sm:px-24"
      {...testIdProp('page-email-designer')}
    >
      <EmptyState
        testId="email-designer-unavailable"
        title={S.notAvailable}
        action={
          <ModuleLink href={FUNDRAISERS_ROUTES.emails(fundraiserId)} testId="email-designer-back">
            {S.backToCampaign}
          </ModuleLink>
        }
      />
    </div>
  );
}

interface DesignerScreenProps {
  fundraiserId: string;
  emailId: string;
  orgRef: string;
  orgName: string;
  campaignName: string;
  position: { index: number; total: number } | null;
  status: string | null;
  /** A duplicate not changed since: its header says "Copy of" beside its subject, never in it. */
  untouchedCopy: boolean;
  /** Why Send test waits (the sending domain isn't ready), or null when a test can go out. */
  testWaits: string | null;
  /** The sending domain's read is still out: Send test waits for it, without a reason yet. */
  senderPending: boolean;
  email: DesignerEmail;
  previewHtml: string | null;
  previewPending: boolean;
  fromName: string;
  fromAddress: string | null;
  orgLogoSrc: string | null;
  designer: NonNullable<ReturnType<typeof useFundraisersModule>['emailDesigner']>;
  /** Whether this person may add to Collateral (Profile's own write) from the photo picker. */
  canUploadCollateral: boolean;
  /** Whether this person may change a scheduled email (the Owner tier). */
  canChangeScheduled: boolean;
  /** The organization's own date, for what the image picker offers. */
  orgToday: string;
  onNavigate: (href: string) => void;
  refresh: () => Promise<{ email: DesignerEmail | null; html: string | null }>;
}

function DesignerScreen({
  fundraiserId,
  emailId,
  orgRef,
  orgName,
  campaignName,
  position,
  status,
  untouchedCopy,
  testWaits,
  senderPending,
  email,
  previewHtml,
  previewPending,
  fromName,
  fromAddress,
  orgLogoSrc,
  designer,
  canUploadCollateral,
  canChangeScheduled,
  orgToday,
  onNavigate,
  refresh,
}: DesignerScreenProps) {
  const scope = useMemo(
    () => ({ org_ref: orgRef, fundraiser_id: fundraiserId, email_id: emailId }),
    [orgRef, fundraiserId, emailId],
  );
  // What's typed to the assistant, and the person's last ask: the page's, not the panel body's, so closing
  // and reopening the panel keeps both — an ask that comes back (a refusal, a turn that can't be
  // retried) returns to the box even if the panel was closed while it ran.
  const [draft, setDraft] = useState('');
  const lastAsk = useRef<string | null>(null);
  const thread = useAssistantThread({
    wireSurface: EMAIL_DESIGNER_WIRE_SURFACE,
    entryContextExtra: scope,
    threadMatch: scope,
    apiBaseUrl: designer.apiBaseUrl,
    authHeaders: designer.authHeaders,
    renderServerOpeningMessage: false,
    active: true,
  });

  // ── What the last change did, and what Undo puts back ─────────────────────────────────────────
  // The undo stack is the page's own: a reload starts it empty, so it never puts back a version older
  // than what someone else may have changed since.
  const [undoStack, setUndoStack] = useSteps();
  // What Undo took back, last first; any new change ends it.
  const [redoStack, setRedoStack] = useSteps();
  // Each Undo leaves a line in the conversation, after the turn it undid, so the chat never ends on a
  // change the email no longer holds (the polish-1 user test). The page's own: a reload starts it empty.
  const [undoneAfter, setUndoneAfter] = useState<number[]>([]);
  const [washAgainst, setWashAgainst] = useState<string | null>(null);
  const before = useRef<Snapshot | null>(null);
  const lastStatus = useRef(thread.status);
  const snapshot = useCallback(
    (): Snapshot | null =>
      email.body === null
        ? null
        : {
            subject: email.subject,
            preheader: email.preheader,
            body: email.body,
            blockCount: email.blockCount,
            html: previewHtml,
          },
    [email, previewHtml],
  );

  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = thread.status;
    const ended =
      (was === 'sending' || was === 'streaming') &&
      (thread.status === 'idle' || thread.status === 'failed');
    if (!ended) return;
    const prior = before.current;
    before.current = null;
    void refresh().then(({ email: next }) => {
      if (prior === null || next === null || sameContent(prior, next)) return;
      // The first draft is not an undo step (14b): there is nothing to go back to but an empty email.
      if (prior.blockCount === 0 && prior.subject === '' && prior.preheader === '') {
        setWashAgainst(null);
        return;
      }
      setUndoStack((stack) => [...stack, { before: prior, after: next, byHand: false }].slice(-20));
      setRedoStack([]);
      setWashAgainst(prior.blockCount > 0 ? prior.html : null);
    });
  }, [thread.status, refresh, setUndoStack, setRedoStack]);

  // ── Hand edits ───────────────────────────────────────────────────────────────────────────────
  // What's open in the hand editor, saved before a turn so the assistant reads it.
  const commitHandEdit = useRef<(() => Promise<boolean> | null) | null>(null);
  const latest = useRef(snapshot);
  latest.current = snapshot;
  // What a hand edit just saved, as the server stored it: a turn that follows at once starts from it,
  // before the page has drawn it.
  const savedByHand = useRef<Snapshot | null>(null);
  // When the email last changed, as this page knows it: moved on as each save comes back, before a draw.
  const readStamp = useRef(email.updatedAt);
  readStamp.current = email.updatedAt;
  // A hand save still on its way, the subject's and the preview text's included.
  const saving = useRef<Promise<unknown> | null>(null);
  const saveByHand = useCallback(
    async (changes: {
      subject?: string;
      preheader?: string;
      body_json?: Record<string, unknown>;
    }) => {
      const save = async () => {
        const prior = latest.current();
        const expected = readStamp.current;
        try {
          await saveDesignerEmail(orgRef, fundraiserId, emailId, {
            ...changes,
            ...(expected === null ? {} : { expected_updated_at: expected }),
          });
        } catch (error) {
          toast.error(refusalWords(error, S.hand.refused));
          return false;
        }
        const { email: next, html } = await refresh();
        readStamp.current = next?.updatedAt ?? null;
        savedByHand.current =
          next === null || next.body === null
            ? null
            : {
                subject: next.subject,
                preheader: next.preheader,
                body: next.body,
                blockCount: next.blockCount,
                html,
              };
        setWashAgainst(null);
        if (prior !== null && next !== null && !sameContent(prior, next)) {
          setUndoStack((stack) =>
            [...stack, { before: prior, after: next, byHand: true }].slice(-20),
          );
          setRedoStack([]);
        }
        return true;
      };
      const done = (saving.current ?? Promise.resolve()).then(save);
      const held = done.finally(() => {
        if (saving.current === held) saving.current = null;
      });
      saving.current = held;
      return done;
    },
    [orgRef, fundraiserId, emailId, refresh, setUndoStack, setRedoStack],
  );

  /** Whatever's open in the hand editor saved, and any save on its way done: true when an act may go on,
   *  false when the server refused the words (they stay open to fix). Null at once when there's nothing
   *  to wait for, so an act starts in the same tick as its press. */
  const whenSaved = useCallback((): Promise<boolean> | null => {
    const open = commitHandEdit.current?.() ?? null;
    const under = saving.current;
    if (open === null && under === null) return null;
    return (async () => {
      const saved = open === null ? true : await open;
      if (saving.current !== null) await saving.current.catch(() => undefined);
      return saved;
    })();
  }, []);
  /** Run `act` once the words are saved; never if they were refused. */
  const afterSaved = useCallback(
    (act: () => void) => {
      const waiting = whenSaved();
      if (waiting === null) act();
      else void waiting.then((saved) => saved && act());
    },
    [whenSaved],
  );

  /** Run `turn` after saving what's open in the hand editor: at once when nothing is (the turn's status
   *  starts in the same tick as the press), else once the save is done, from what it saved. A save the
   *  server refused holds the turn back (false), its words left open to fix. */
  const afterHandEdit = useCallback(
    (turn: () => void): boolean | Promise<boolean> => {
      const waiting = whenSaved();
      if (waiting === null) {
        before.current = latest.current();
        turn();
        return true;
      }
      savedByHand.current = null;
      return waiting.then((saved) => {
        if (!saved) return false;
        before.current = savedByHand.current ?? latest.current();
        turn();
        return true;
      });
    },
    [whenSaved],
  );
  const send = useCallback(
    (text: string) => afterHandEdit(() => thread.send(text)),
    [afterHandEdit, thread],
  );
  // "Try again" is a new turn too: what the email held before it is its undo step and its wash's base.
  const retry = useCallback(() => afterHandEdit(() => thread.retry()), [afterHandEdit, thread]);

  const undo = useMutation({
    mutationFn: async () => {
      if ((await whenSaved()) === false) return null;
      // Read after the save: what was open a moment ago is now the last step.
      const last = undoStack.current.at(-1);
      if (last === undefined) return null;
      // Never over a change made elsewhere since (the organization's own AI on the connector, another
      // tab): the email must still be what the assistant's change left.
      const now = decodeDesignerEmail(await fetchDesignerEmail(orgRef, fundraiserId, emailId));
      if (now === null || !sameContent(last.after, now)) throw new ChangedSinceError();
      await restoreDesignerEmail(orgRef, fundraiserId, emailId, {
        subject: last.before.subject,
        preheader: last.before.preheader,
        body_json: last.before.body,
      });
      return last;
    },
    onSuccess: async (last) => {
      if (last === null) return;
      setWashAgainst(null);
      const { email: restored } = await refresh();
      if (restored !== null && restored.body !== null) {
        setRedoStack((stack) => [
          ...stack,
          { before: stepOf(restored, previewHtml), after: last.after, byHand: last.byHand },
        ]);
      }
      // The step below now starts from what was put back, as the server stored it.
      setUndoStack((stack) => {
        const rest = stack.slice(0, -1);
        const below = rest.at(-1);
        return below === undefined || restored === null
          ? rest
          : [...rest.slice(0, -1), { ...below, after: restored }];
      });
      // A hand edit has no turn in the conversation to mark as undone.
      if (!last.byHand) setUndoneAfter((marks) => [...marks, thread.messages.length]);
      toast.success(S.undone);
    },
    onError: (error) => {
      if (error instanceof ChangedSinceError) {
        setUndoStack([]);
        setRedoStack([]);
        toast.error(S.changedSince);
        return;
      }
      toast.error(S.undoFailed);
    },
  });

  const redo = useMutation({
    mutationFn: async () => {
      if ((await whenSaved()) === false) return null;
      const last = redoStack.current.at(-1);
      if (last === undefined) return null;
      const now = decodeDesignerEmail(await fetchDesignerEmail(orgRef, fundraiserId, emailId));
      if (now === null || !sameContent(last.before, now)) throw new ChangedSinceError();
      await restoreDesignerEmail(orgRef, fundraiserId, emailId, {
        subject: last.after.subject,
        preheader: last.after.preheader,
        body_json: last.after.body ?? {},
      });
      return last;
    },
    onSuccess: async (last) => {
      if (last === null) return;
      setWashAgainst(null);
      const { email: restored } = await refresh();
      setRedoStack((stack) => stack.slice(0, -1));
      if (restored !== null) {
        setUndoStack((stack) => [...stack, { ...last, after: restored }]);
        if (!last.byHand) setUndoneAfter((marks) => marks.slice(0, -1));
      }
    },
    onError: (error) => {
      if (error instanceof ChangedSinceError) {
        setUndoStack([]);
        setRedoStack([]);
        toast.error(S.changedSince);
        return;
      }
      toast.error(S.undoFailed);
    },
  });

  const test = useMutation({
    mutationFn: () => sendDesignerTest(orgRef, fundraiserId, emailId),
    onSuccess: (body) => {
      const to = decodeDesignerTest(body);
      if (to !== null) toast.success(S.testEmailed(to));
    },
    onError: (error) => {
      toast.error(refusalWords(error, S.testRefused));
    },
  });

  // ── The header ───────────────────────────────────────────────────────────────────────────────
  const empty = designerEmailEmpty(email);
  const changeable = designerEmailChangeable(email);
  const busy = thread.status === 'sending' || thread.status === 'streaming';
  const when = email.scheduledFor === null ? null : emailTimeLabel(email.scheduledFor, email.tz);
  const meta = [
    position === null ? null : S.position(position.index, position.total),
    status,
    when ?? EMAILS_COPY.noTime,
    empty ? null : S.saved,
  ].filter((part): part is string => part !== null);

  return (
    <div
      className="fundraisers-module flex w-full flex-col gap-20 px-16 py-20 sm:px-24 xl:px-[26px]"
      {...testIdProp('page-email-designer')}
    >
      {/* The way back above the title and the meta line under it, at EVERY width: the assistant's panel
          opens and closes beside the page, and a header that re-flowed onto one line as it closed made
          the title jump (the unified-panel user test, 2026-11-01). Where the title would have under
          260px (a 1024–1180 window with the panel docked), the buttons wrap below it rather than the
          subject truncating to a word. */}
      <header className="flex flex-wrap items-center gap-x-12 gap-y-8">
        <div className="min-w-[260px] flex-1">
          <div className="flex flex-col items-start gap-y-2">
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              // Stacked above the title, the way back lines its glyph up with the title's first letter.
              className="-ml-12"
              leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
              onClick={() => afterSaved(() => onNavigate(FUNDRAISERS_ROUTES.emails(fundraiserId)))}
              testId="email-designer-back"
            >
              {campaignName}
            </Button>
            <h1
              className="min-w-0 max-w-full truncate font-sans text-s22 font-w700 leading-h13 tracking-givmo text-text-strong"
              {...testIdProp('email-designer-title')}
            >
              {untouchedCopy && (
                <span
                  className="font-w400 text-text-tertiary"
                  {...testIdProp('email-designer-copy-of')}
                >
                  {EMAILS_COPY.copyOf}{' '}
                </span>
              )}
              {email.subject.trim() === '' ? S.untitled : email.subject}
            </h1>
            {meta.length > 0 && (
              <span
                className="font-sans text-s14 tracking-givmo text-text-tertiary"
                {...testIdProp('email-designer-meta')}
              >
                {meta.join(' · ')}
              </span>
            )}
          </div>
        </div>
        {/* Why Send test waits sits under the buttons, in the gap above the cards on a wide screen, so
            the title row keeps its buttons (review-polish A-1). */}
        <div className="ml-auto flex shrink-0 flex-col items-end gap-4 lg:relative">
          <div className="flex items-center gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              size="lg"
              leadingIcon={<Icon icon={Send} size={16} aria-hidden />}
              disabled={empty || busy || senderPending || testWaits !== null}
              aria-describedby={testWaits !== null ? 'email-designer-test-waits' : undefined}
              loading={test.isPending}
              onClick={() => afterSaved(() => test.mutate())}
              testId="email-designer-send-test"
            >
              {S.sendTest}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              // The audience and the date wait on nothing the words need (the KCC dev build,
              // 2026-10-31: a series is planned before it is written); Schedule still needs both.
              disabled={busy}
              onClick={() =>
                afterSaved(() =>
                  onNavigate(FUNDRAISERS_ROUTES.email(fundraiserId, emailId, 'audience')),
                )
              }
              testId="email-designer-continue"
            >
              {S.continue}
            </Button>
          </div>
          {testWaits !== null && (
            <p
              id="email-designer-test-waits"
              className="text-right font-sans text-s12 tracking-givmo text-text-secondary lg:absolute lg:top-full lg:right-0 lg:mt-3 lg:whitespace-nowrap"
              {...testIdProp('email-designer-test-waits')}
            >
              {testWaits}
            </p>
          )}
        </div>
      </header>

      {/* The assistant is the console's panel (the Emails editor's scope opens it by default,
          beside the page): its conversation is drawn there, and the preview takes the page. */}
      <AssistantPageBody>
        <AssistantCard
          thread={thread}
          draft={draft}
          setDraft={setDraft}
          lastAsk={lastAsk}
          onSend={send}
          onRetry={retry}
          empty={empty}
          changeable={changeable}
          undoneAfter={undoneAfter}
          orgRef={orgRef}
          orgName={orgName}
          designer={designer}
        />
      </AssistantPageBody>
      <div className="flex min-h-0 flex-col lg:h-[calc(100dvh-196px)] lg:min-h-[640px]">
        <PreviewCard
          html={previewHtml}
          pending={previewPending}
          empty={email.blockCount === 0}
          subject={email.subject}
          preheader={email.preheader}
          fromName={fromName}
          fromAddress={fromAddress}
          orgLogoSrc={orgLogoSrc}
          orgName={orgName}
          washAgainst={washAgainst}
          hand={{
            enabled:
              changeable &&
              (email.state !== 'scheduled' || canChangeScheduled) &&
              !busy &&
              email.body !== null &&
              !undo.isPending &&
              !redo.isPending,
            body: email.body,
            orgRef,
            today: orgToday,
            canUploadCollateral,
            commitRef: commitHandEdit,
            onSaveBody: (body) => saveByHand({ body_json: body }),
            onSaveSubject: (subject) => saveByHand({ subject }),
            onSavePreheader: (preheader) => saveByHand({ preheader }),
          }}
          history={{
            canUndo: undoStack.current.length > 0 && changeable && !busy,
            canRedo: redoStack.current.length > 0 && changeable && !busy,
            undoing: undo.isPending,
            redoing: redo.isPending,
            onUndo: () => undo.mutate(),
            onRedo: () => redo.mutate(),
          }}
          whenSaved={afterSaved}
        />
      </div>
    </div>
  );
}

// ── The assistant ──────────────────────────────────────────────────────────────────────────────────

function AssistantCard({
  thread,
  draft,
  setDraft,
  lastAsk,
  onSend,
  onRetry,
  empty,
  changeable,
  undoneAfter,
  orgRef,
  orgName,
  designer,
}: {
  thread: ReturnType<typeof useAssistantThread>;
  /** What's typed, held by the page so closing the panel never loses it. */
  draft: string;
  setDraft: (next: string | ((current: string) => string)) => void;
  /** The person's last ask, held by the page for the same reason. */
  lastAsk: { current: string | null };
  onSend: (text: string) => boolean | Promise<boolean>;
  onRetry: () => void;
  empty: boolean;
  changeable: boolean;
  /** After how many messages each Undo came. */
  undoneAfter: number[];
  orgRef: string;
  orgName: string;
  designer: DesignerScreenProps['designer'];
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  // The box grows with what's typed, up to six lines, then scrolls.
  useEffect(() => {
    const node = input.current;
    if (node === null) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 152)}px`;
  }, [draft]);
  const busy = thread.status === 'sending' || thread.status === 'streaming';

  // The organization's: the send's refusal carries its sheet.
  const accept = useAcceptOrgAssistantConsent(orgRef);
  const orgSheet =
    thread.consentRefusal === null ? null : decodeOrgConsent(thread.consentRefusal.state);
  const [sheetOpen, setSheetOpen] = useState(true);
  useEffect(() => {
    if (thread.consentRefusal !== null) setSheetOpen(true);
  }, [thread.consentRefusal]);
  // An ask that won't go now (a person who can't turn the assistant on, a usage limit) goes back in the
  // box, so it is never lost.
  const blockedAsk = thread.consentRefusal?.text ?? null;
  const heldBack =
    (blockedAsk !== null && (orgSheet === null || !designer.canAcceptForOrg)) ||
    thread.failure?.retryable === false;
  useEffect(() => {
    if (!heldBack) return;
    const text = blockedAsk ?? lastAsk.current;
    if (text !== null) setDraft((current) => (current.trim().length === 0 ? text : current));
  }, [heldBack, blockedAsk, setDraft, lastAsk]);

  useEffect(() => {
    const node = scroller.current;
    if (node !== null) node.scrollTop = node.scrollHeight;
  }, [thread.messages.length, thread.streamingText, thread.status, undoneAfter.length]);

  const submit = () => {
    const text = draft.trim();
    if (text.length === 0 || busy || !changeable) return;
    lastAsk.current = text;
    const asked = onSend(text);
    setDraft('');
    // Held back by a hand edit the server refused: the ask goes back in the box, never lost.
    if (asked instanceof Promise) {
      void asked.then((went) => {
        if (!went) setDraft((current) => (current.trim().length === 0 ? text : current));
      });
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const blockedByOrg = thread.consentRefusal !== null;
  const showEmpty = thread.messages.length === 0 && !busy && !blockedByOrg;

  return (
    // The panel's body: the panel draws the title and the close above it.
    <div className="flex min-h-0 flex-1 flex-col" {...testIdProp('email-designer-assistant')}>
      <div ref={scroller} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-20 py-20">
        {showEmpty ? (
          <div
            className="m-auto flex max-w-[360px] flex-col items-center gap-8 text-center"
            {...testIdProp('email-designer-empty')}
          >
            <span className="mb-8 flex size-44 items-center justify-center rounded-full bg-brand-50">
              <GivmoLogomark className="size-[24px] text-brand-600" title="" aria-hidden />
            </span>
            <p className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
              {empty ? S.emptyTitle : S.filledTitle}
            </p>
            <p className="font-sans text-s15 leading-h15 tracking-givmo text-text-secondary">
              {empty ? S.emptyBody : S.filledBody}
            </p>
          </div>
        ) : (
          <div className="mt-auto flex flex-col gap-20" {...testIdProp('email-designer-thread')}>
            {thread.messages
              .map((message) => <DesignerMessage key={message.key} message={message} />)
              .flatMap((node, index) => [
                node,
                ...undoneAfter
                  .map((at, mark) => ({ at, mark }))
                  .filter(({ at }) => at === index + 1)
                  .map(({ mark }) => (
                    <p
                      key={`undone-${mark}`}
                      className="flex items-center gap-6 font-sans text-s13 tracking-givmo text-text-secondary"
                      {...testIdProp('email-designer-undone')}
                    >
                      <Icon icon={RotateCcw} size={13} aria-hidden />
                      {S.undoneLine}
                    </p>
                  )),
              ])}
            {busy && (
              <div className="font-sans text-s14 leading-h15 tracking-givmo text-text">
                {thread.streamingText.length > 0 ? (
                  <ReplyText text={thread.streamingText} />
                ) : (
                  <span className="text-text-tertiary" {...testIdProp('email-designer-working')}>
                    {S.working}
                  </span>
                )}
              </div>
            )}
            {thread.status === 'failed' &&
              (thread.failure?.retryable === false ? (
                <p
                  className="font-sans text-s13 tracking-givmo text-text-secondary"
                  {...testIdProp('email-designer-refused')}
                >
                  {thread.failure.message ?? S.failed}
                </p>
              ) : (
                <p className="font-sans text-s13 tracking-givmo text-text-secondary">
                  {thread.failure?.message ?? S.failed}{' '}
                  <button
                    type="button"
                    className="font-w600 text-brand-600 hover:underline"
                    onClick={onRetry}
                    {...testIdProp('email-designer-retry')}
                  >
                    {S.tryAgain}
                  </button>
                </p>
              ))}
            {blockedAsk !== null && (
              <div
                className="max-w-[86%] self-end whitespace-pre-line rounded-all10 bg-neutral-100 px-16 py-10 font-sans text-s15 leading-h15 tracking-givmo text-text"
                {...testIdProp('email-designer-blocked-ask')}
              >
                {blockedAsk}
              </div>
            )}
            {blockedByOrg && (orgSheet === null || !designer.canAcceptForOrg) && (
              <p
                className="font-sans text-s13 tracking-givmo text-text-secondary"
                {...testIdProp('email-designer-consent-blocked')}
              >
                {orgSheet === null ? S.unavailable : S.needsAdmin(orgName)}
              </p>
            )}
            {blockedByOrg && orgSheet !== null && designer.canAcceptForOrg && !sheetOpen && (
              <p
                className="font-sans text-s13 tracking-givmo text-text-secondary"
                {...testIdProp('email-designer-consent-needed')}
              >
                {S.consentNeeded(orgName)}{' '}
                <button
                  type="button"
                  className="font-w600 text-brand-600 hover:underline"
                  onClick={() => setSheetOpen(true)}
                  {...testIdProp('email-designer-turn-on')}
                >
                  {S.turnOn}
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border-subtle px-20 pb-16 pt-16">
        <div
          className={cn(
            'flex items-end gap-8 rounded-lg border border-border-strong bg-surface px-16 py-10',
            'focus-within:border-brand-600 focus-within:shadow-ring',
          )}
        >
          <textarea
            ref={input}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            disabled={!changeable}
            placeholder={!changeable ? S.locked : empty ? S.firstPlaceholder : S.changePlaceholder}
            aria-label={empty ? S.emptyTitle : S.changePlaceholder}
            className="min-h-[44px] flex-1 resize-none bg-transparent py-3 font-sans text-s15 leading-h15 tracking-givmo text-text outline-none placeholder:text-text-tertiary disabled:cursor-not-allowed"
            {...testIdProp('email-designer-input')}
          />
          {busy ? (
            <button
              type="button"
              aria-label={S.stop}
              onClick={() => thread.stop()}
              className="flex size-36 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-text-strong hover:bg-neutral-150"
              {...testIdProp('email-designer-stop')}
            >
              <Icon icon={Square} size={14} aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              aria-label={S.send}
              onClick={submit}
              disabled={draft.trim().length === 0 || !changeable}
              className="flex size-36 shrink-0 items-center justify-center rounded-md bg-brand-600 text-on-brand hover:bg-brand-700 disabled:bg-neutral-100 disabled:text-neutral-400"
              {...testIdProp('email-designer-send')}
            >
              <Icon icon={ArrowUp} size={18} aria-hidden />
            </button>
          )}
        </div>
        <p className="mt-10 font-sans text-s12 tracking-givmo text-text-tertiary">
          {S.connectorLine}
        </p>
      </div>

      {orgSheet !== null && designer.canAcceptForOrg && (
        <AssistantConsentCard
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          instrument={orgSheet}
          orgName={orgName}
          vocab={DESIGNER_VOCAB}
          policyLink={
            DESIGNER_VOCAB.policyLinkPhrase === null
              ? null
              : { phrase: DESIGNER_VOCAB.policyLinkPhrase, href: designer.privacyPolicyUrl }
          }
          accepting={accept.isPending}
          onAccept={(hash) =>
            accept.mutate(hash, {
              onSuccess: () => {
                setSheetOpen(false);
                thread.clearConsentRefusal();
                // The ask the organization's consent held goes now, in the person's own words.
                if (blockedAsk !== null) onSend(blockedAsk);
              },
            })
          }
          testId="email-designer-org-consent"
        />
      )}
    </div>
  );
}

function ReplyText({ text }: { text: string }) {
  const { reply: withChanges, sources } = splitSourcesLine(text);
  const { reply, changes } = splitChangesLine(withChanges);
  return (
    <div className="flex flex-col gap-8">
      {sources !== null && (
        <p
          className="flex items-center gap-6 font-sans text-s13 tracking-givmo text-text-tertiary"
          {...testIdProp('email-designer-sources')}
        >
          <Icon icon={Sparkles} size={13} aria-hidden />
          {sources}
        </p>
      )}
      {reply.length > 0 && (
        <p className="whitespace-pre-line font-sans text-s15 leading-h15 tracking-givmo text-text">
          {reply}
        </p>
      )}
      {changes !== null && (
        <p
          className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('email-designer-changes')}
        >
          <span className="font-w600 text-text-strong">{S.changesLabel}</span> {changes}
        </p>
      )}
    </div>
  );
}

function DesignerMessage({ message }: { message: ThreadMessage }) {
  if (message.role === 'user') {
    return (
      <div
        className="max-w-[86%] self-end whitespace-pre-line rounded-all10 bg-neutral-100 px-16 py-10 font-sans text-s15 leading-h15 tracking-givmo text-text"
        {...testIdProp('email-designer-ask')}
      >
        {message.text}
      </div>
    );
  }
  return (
    <div {...testIdProp('email-designer-reply')}>
      <ReplyText text={message.text} />
    </div>
  );
}

// ── The preview ────────────────────────────────────────────────────────────────────────────────────

function PreviewCard({
  html,
  pending,
  empty,
  subject,
  preheader,
  fromName,
  fromAddress,
  orgLogoSrc,
  orgName,
  washAgainst,
  hand,
  history,
  whenSaved,
}: {
  html: string | null;
  pending: boolean;
  empty: boolean;
  subject: string;
  preheader: string;
  fromName: string;
  fromAddress: string | null;
  orgLogoSrc: string | null;
  orgName: string;
  washAgainst: string | null;
  hand: {
    enabled: boolean;
    body: Record<string, unknown> | null;
    orgRef: string;
    today: string;
    canUploadCollateral: boolean;
    commitRef: MutableRefObject<(() => Promise<boolean> | null) | null>;
    onSaveBody: (body: Record<string, unknown>) => Promise<boolean>;
    onSaveSubject: (subject: string) => Promise<boolean>;
    onSavePreheader: (preheader: string) => Promise<boolean>;
  };
  history: {
    canUndo: boolean;
    canRedo: boolean;
    undoing: boolean;
    redoing: boolean;
    onUndo: () => void;
    onRedo: () => void;
  };
  /** Run an act of the preview's own once what's open in the hand editor has saved. */
  whenSaved: (act: () => void) => void;
}) {
  const [width, setWidth] = useState<'desktop' | 'phone'>('desktop');
  // Cmd/Ctrl+Z and Shift+Cmd/Ctrl+Z are Undo and Redo anywhere on the page but a field, where the browser's
  // own undo is the field's (the KCC hand edit, 2026-11-01). The frame hears its own keys and asks here.
  const historyNow = useRef(history);
  historyNow.current = history;
  // While words are open the keys are theirs, wherever focus sits (their bar in the tools row included): the
  // page's Undo would save the words and then undo that save.
  const [wordsOpen, setWordsOpen] = useState(false);
  const wordsOpenNow = useRef(wordsOpen);
  wordsOpenNow.current = wordsOpen;
  // Words opening for a hand edit: the assistant's SHEET over a narrow page would cover their bar, its
  // Done and the words themselves, so it gets out of the way (docked beside the page, it stays).
  useEffect(() => {
    if (wordsOpen) yieldAssistantSheet();
  }, [wordsOpen]);
  // The tools row above the preview, where the words' bar sits: outside the email, so it covers none of it.
  const [toolsSlot, setToolsSlot] = useState<HTMLDivElement | null>(null);
  const runHistory = useCallback((which: 'undo' | 'redo') => {
    const now = historyNow.current;
    if (wordsOpenNow.current || now.undoing || now.redoing) return;
    if (which === 'undo' && now.canUndo) now.onUndo();
    if (which === 'redo' && now.canRedo) now.onRedo();
  }, []);
  useEffect(() => {
    const key = (event: globalThis.KeyboardEvent) => {
      const which = historyKeyOf(event);
      if (which === null) return;
      event.preventDefault();
      runHistory(which);
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [runHistory]);
  // An email with words in it is edited where it reads; an empty one is the assistant's to start.
  const handOn = hand.enabled && !(empty && subject.trim() === '' && preheader.trim() === '');
  const hasInboxLine = handOn || subject.trim() !== '' || preheader.trim() !== '';

  return (
    <Card
      flush
      className="flex min-h-[640px] flex-col overflow-hidden lg:min-h-0"
      testId="email-designer-preview"
    >
      {/* The controls keep their words at every width. Beside the docked assistant (a 1024–1180
          window) the card is narrower than one row of them, so the row wraps: the width switch goes to a
          second line under Undo and Redo rather than anything losing its words or running off the card. */}
      <div className="flex min-h-[60px] shrink-0 flex-wrap items-center gap-x-10 gap-y-8 border-b border-border-subtle px-24 py-10">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {S.preview}
        </h2>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-10 gap-y-8">
          <div className="flex items-center gap-2">
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              className="text-text-strong"
              leadingIcon={<Icon icon={RotateCcw} size={14} aria-hidden />}
              disabled={!history.canUndo}
              loading={history.undoing}
              onClick={history.onUndo}
              testId="email-designer-undo"
            >
              {S.undo}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              className="text-text-strong"
              leadingIcon={<Icon icon={RotateCw} size={14} aria-hidden />}
              disabled={!history.canRedo}
              loading={history.redoing}
              onClick={history.onRedo}
              testId="email-designer-redo"
            >
              {S.redo}
            </Button>
          </div>
          <div
            role="radiogroup"
            aria-label={S.previewWidth}
            className="flex items-center gap-2 rounded-md border border-border-subtle bg-neutral-50 p-3"
          >
            {(
              [
                ['desktop', S.desktop, Monitor],
                ['phone', S.phone, Smartphone],
              ] as const
            ).map(([value, label, glyph]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={width === value}
                onClick={() => whenSaved(() => setWidth(value))}
                className={cn(
                  'inline-flex h-32 items-center gap-6 whitespace-nowrap rounded-[6px] px-12 font-sans text-s14 tracking-givmo',
                  width === value
                    ? 'bg-surface font-w600 text-text-strong shadow-xs'
                    : 'text-text-secondary hover:text-text-strong',
                )}
                {...testIdProp(`email-designer-width-${value}`)}
              >
                <Icon icon={glyph} size={15} aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="shrink-0 border-b border-border-subtle px-24 py-14"
        {...testIdProp('email-designer-inbox')}
      >
        <p className="font-sans text-s12 font-w600 uppercase tracking-[0.04em] text-text-tertiary">
          {S.inTheInbox}
        </p>
        <div className="mt-8 flex items-start gap-12">
          {orgLogoSrc !== null ? (
            // The organization's own square logo, as an inbox shows a sender (14b draws KCC's mark) —
            // never the email's wide wordmark shrunk into a circle.
            <span className="flex size-[40px] shrink-0 overflow-hidden rounded-full border border-border-subtle bg-surface p-[4px]">
              <img
                src={orgLogoSrc}
                alt=""
                className="h-full w-full object-contain"
                {...testIdProp('email-designer-inbox-logo')}
              />
            </span>
          ) : (
            <Avatar name={fromName} size="md" />
          )}
          <div className="min-w-0">
            <p className="truncate font-sans text-s14 tracking-givmo">
              <span className="font-w600 text-text-strong">{fromName}</span>
              {fromAddress !== null && (
                <span className="ml-6 text-text-tertiary">{fromAddress}</span>
              )}
            </p>
            {hasInboxLine ? (
              <>
                <InboxLineField
                  value={subject}
                  label={S.hand.subject}
                  placeholder={S.hand.subjectPlaceholder}
                  maxLength={200}
                  enabled={handOn}
                  strong
                  onSave={hand.onSaveSubject}
                  testId="email-designer-subject"
                />
                <InboxLineField
                  value={preheader}
                  label={S.hand.preheader}
                  placeholder={S.hand.preheaderPlaceholder}
                  maxLength={200}
                  enabled={handOn}
                  strong={false}
                  caption={S.hand.preheader}
                  onSave={hand.onSavePreheader}
                  testId="email-designer-preheader"
                />
              </>
            ) : (
              <p className="font-sans text-s14 tracking-givmo text-text-secondary">
                {S.inboxEmpty}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* The tools row: the hint while nothing is open, the words' bar while words are. Always the same
          height, so opening words moves nothing under the pointer. */}
      {handOn && (
        <div
          className="flex min-h-[48px] shrink-0 items-center border-b border-border-subtle px-20 py-6"
          {...testIdProp('email-designer-tools')}
        >
          <div ref={setToolsSlot} className="contents" />
          {!wordsOpen && (
            <span
              className="px-4 font-sans text-s13 tracking-givmo text-text-tertiary"
              {...testIdProp('email-designer-hand-hint')}
            >
              {S.hand.hint}
            </span>
          )}
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col bg-neutral-50 px-24 py-18">
        {html !== null ? (
          <EmailPreviewStage
            html={html}
            subject={subject}
            width={width}
            empty={empty}
            orgName={orgName}
            washAgainst={washAgainst}
            testIdPrefix="email-designer"
            title={S.preview}
            overlay={
              hand.body === null
                ? undefined
                : (frame) => (
                    <HandEditLayer
                      {...frame}
                      body={hand.body as Record<string, unknown>}
                      enabled={handOn}
                      orgRef={hand.orgRef}
                      today={hand.today}
                      canUploadCollateral={hand.canUploadCollateral}
                      onSave={hand.onSaveBody}
                      commitRef={hand.commitRef}
                      onHistoryKey={runHistory}
                      toolsSlot={handOn ? toolsSlot : null}
                      onWordsOpen={setWordsOpen}
                    />
                  )
            }
          />
        ) : (
          <p className="py-48 text-center font-sans text-s14 text-text-tertiary">
            {pending ? S.previewLoading : S.previewUnreadable}
          </p>
        )}
      </div>
    </Card>
  );
}
